import React from "react";
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight, Plus, Sparkles, Trash2 } from "lucide-react";
import { ActionMenu, ActionMenuDivider, ActionMenuItem, AppButton, AppCard, IconButton, Input } from "../ui";
import { formatMinutes } from "./syllabusModel";
import TopicRow from "./TopicRow";

/**
 * One module of a module-wise syllabus: its title, a one-line summary (topics ·
 * time · how many classes it makes), and — open — its topics.
 *
 * Props:
 *   module, index, count, open, onToggle
 *   classCount         — classes this module forms at the current length
 *   sessionCount       — its Smart Learning sessions (null when sessions are not hand-written)
 *   errors             — validation messages by uid
 *   openTopics, onToggleTopic(uid)
 *   focusUid           — a topic just added (focus it)
 *   onChange(patch), onMove(delta), onRemove()
 *   onTopicChange(uid, patch), onTopicAddAfter(uid), onTopicMove(uid, delta), onTopicRemove(uid)
 *   topicResources(topic) → TopicRow's `resources` prop or null
 *   children           — rendered under the topics (the module's sessions)
 */
const ModuleCard = ({
  module, index, count, open, onToggle, classCount, sessionCount = null, errors = {},
  openTopics, onToggleTopic, focusUid,
  onChange, onMove, onRemove,
  onTopicChange, onTopicAddAfter, onTopicMove, onTopicRemove,
  topicResources, children,
}) => {
  const titled = module.topics.filter((t) => t.title.trim());
  const minutes = titled.reduce((n, t) => n + (Number(t.minutes) || 0), 0);
  const name = module.title.trim() || `Module ${index + 1}`;
  const error = errors[module.uid];
  const topicsError = errors[`${module.uid}:topics`];

  return (
    <AppCard as="section" aria-label={`Module ${index + 1}: ${name}`} overflowVisible>
      <div className="tw-flex tw-flex-col tw-gap-3 tw-p-4">
        <div className="tw-flex tw-items-start tw-gap-2">
          <IconButton
            size="sm"
            icon={open ? ChevronDown : ChevronRight}
            label={open ? `Collapse ${name}` : `Expand ${name}`}
            aria-expanded={open}
            onClick={onToggle}
            className="tw-mt-1.5"
          />
          <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-1">
            <div className="tw-flex tw-items-center tw-gap-2">
              <span className="tw-shrink-0 tw-rounded-sm tw-bg-primary/10 tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold tw-text-primary">
                Module {index + 1}
              </span>
              <Input
                size="lg"
                value={module.title}
                invalid={!!error}
                onChange={(e) => onChange({ title: e.target.value })}
                placeholder="Module title — e.g. Core Java"
                aria-label={`Module ${index + 1} title`}
                maxLength={200}
                autoFocus={focusUid === module.uid}
                className="tw-font-semibold"
              />
            </div>
            {error && <p role="alert" className="tw-m-0 tw-text-xs tw-text-danger">{error}</p>}
            <p className="tw-m-0 tw-text-xs tw-text-content-muted">
              {titled.length} topic{titled.length === 1 ? "" : "s"} · {formatMinutes(minutes)}
              {classCount != null && ` · ${classCount} class${classCount === 1 ? "" : "es"}`}
              {sessionCount != null && (
                <span className="tw-ms-1.5 tw-inline-flex tw-items-center tw-gap-1 tw-rounded-sm tw-bg-primary/10 tw-px-1.5 tw-py-0.5 tw-font-semibold tw-text-primary">
                  <Sparkles size={11} aria-hidden="true" /> {sessionCount} Smart session{sessionCount === 1 ? "" : "s"}
                </span>
              )}
            </p>
          </div>
          <ActionMenu label={`Actions for ${name}`}>
            <ActionMenuItem onClick={() => onMove(-1)} disabled={index === 0}><ArrowUp size={14} aria-hidden="true" /> Move module up</ActionMenuItem>
            <ActionMenuItem onClick={() => onMove(1)} disabled={index === count - 1}><ArrowDown size={14} aria-hidden="true" /> Move module down</ActionMenuItem>
            <ActionMenuDivider />
            <ActionMenuItem danger onClick={onRemove}><Trash2 size={14} aria-hidden="true" /> Delete module</ActionMenuItem>
          </ActionMenu>
        </div>

        {open && (
          <div className="tw-flex tw-flex-col tw-gap-3 tw-ps-1 min-[576px]:tw-ps-10">
            {topicsError && <p role="alert" className="tw-m-0 tw-text-xs tw-text-danger">{topicsError}</p>}
            <ol className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-2 tw-p-0">
              {module.topics.map((t, i) => (
                <TopicRow
                  key={t.uid}
                  topic={t}
                  index={i}
                  count={module.topics.length}
                  open={openTopics.has(t.uid)}
                  onToggle={() => onToggleTopic(t.uid)}
                  error={errors[t.uid]}
                  autoFocus={focusUid === t.uid}
                  onChange={(patch) => onTopicChange(t.uid, patch)}
                  onAddAfter={() => onTopicAddAfter(t.uid)}
                  onMove={(delta) => onTopicMove(t.uid, delta)}
                  onRemove={() => onTopicRemove(t.uid)}
                  resources={topicResources ? topicResources(t) : null}
                />
              ))}
            </ol>
            <div>
              <AppButton size="sm" variant="ghost" onClick={() => onTopicAddAfter(module.topics[module.topics.length - 1]?.uid)}>
                <Plus size={14} aria-hidden="true" /> Add topic
              </AppButton>
            </div>
            {children}
          </div>
        )}
      </div>
    </AppCard>
  );
};

export default ModuleCard;
